import { Component, Input, ChangeDetectorRef, Output, EventEmitter, forwardRef, OnInit } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, filter } from 'rxjs';
import { CheckboxTypesEnum } from '../../enums/checkbox-types.enum';


/**
 * ### Accessibility Usage
 * + ***ariaDescribedBy*** - use this if the checkbox has an external error message, or it needs extra description for screen reader. Use message's id
 * + ***ariaLabelledBy*** - use this to provide an accessible label for the checkbox, only when the label is outside the checkbox
 * + ***hiddenLabel*** - use this to add more information to checkbox's screen reader description, for example linking to entity's name
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 * + ***initiallyFocusable*** - use this if the focusablity of the checkbox can vary and an initial state is needed. Usually used in a table and set to false
 * + ***tabindex*** - use this if the parent wants to directly control the checkbox's focusability. This is not preferred as disabled handles it,
 *                    and we use initiallyFocusable if the checkbox is in a table
 * + ***tooltip*** - use this to display a visual tooltip of the checkbox. Use it only when the visible label is non-existent,
 *                   and the purpose of the checkbox is not straightforward, e.g. briefing style bold button
 */
@Component({
    selector: 'agpr-checkbox',
    templateUrl: 'checkbox.component.html',
    styleUrls: ['./checkbox.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => CheckboxComponent),
        multi: true
    }],
    standalone: false
})
export class CheckboxComponent implements ControlValueAccessor, OnInit {
    @Input() id: string;
    @Input() label: string;
    @Input() name: string;
    @Input() value: any;
    @Input() tabindex: number;
    @Input() disabled: boolean;
    @Input() canBeDisabledWhenChecked: boolean = true;
    @Input() bottomMargin: boolean = false;
    @Input() binary: boolean;
    @Input() formControl: UntypedFormControl;
    @Input() readonly: boolean;
    @Input() icon: string;
    @Input() iconTooltip: string;
    @Input() style: CheckboxTypesEnum = CheckboxTypesEnum.normal;
    @Input() tooltip: string;
    @Input() hiddenLabel: string;
    @Input() ariaLabelledBy: string;
    @Input() ariaDescribedBy: string;
    @Input() infoCircleTooltip: string;
    @Input() tooltipPosition?: string = 'right';
    @Input() partialSelected: boolean;
    @Input() initiallyFocusable: boolean = true;
    @Input() labelStyleClass: string = '';

    @Input() labelLookupKey: string;
    @Input() labelLookupValue: string = null;
    @Input() labelLookup: (keys: any[]) => Observable<any[]> | Observable<any[]> = null;

    /** iconBadge only */
    @Input() imgLabel?: string;
    @Input() iconLabel?: string;
    @Input() checkedIcon?: string;
    @Input() iconBadgeTooltip?: string;
    /** iconBadge only */

    // TODO: disabled property collides with FormControl's disabled property, so we need to use a different name
    // should eventually replace disabled in all places to avoid console warning
    @Input()
    set isDisabled(isDisabled: boolean) {
        this.disabled = isDisabled;
    }

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onChange: EventEmitter<any> = new EventEmitter();
    @Output() blur: EventEmitter<FocusEvent> = new EventEmitter();

    /** either boolean or array of values */
    model: any;

    checked: boolean;
    focused: boolean;

    checkboxTypesEnum = CheckboxTypesEnum;

    constructor(private cd: ChangeDetectorRef) {}

    onModelChange: Function = () => {};
    onModelTouched: Function = () => {};

    /**
     * Handles click event on label or visible checkbox icon
     */
    onClick(event: Event): void {
        event.preventDefault();
        if (this.disabled && (!this.checked || this.canBeDisabledWhenChecked) || this.readonly) {
            return;
        }

        this.checked = !this.checked;
        this.updateModel();
    }

    /**
     * Updates model depending on state of checkbox
     */
    updateModel(): void {
        if (!this.binary) {
            if (this.checked) {
                this.addValue();
            } else {
                this.removeValue();
            }

            this.onModelChange(this.model);

            if (this.formControl) {
                this.formControl.setValue(this.model);
            }
        } else {
            this.onModelChange(this.checked);
        }

        this.onChange.emit(this.checked);
    }

    /**
     * Handles change to checkbox element
     */
    handleChange(event): void {
        if (!this.readonly) {
            this.checked = event.target.checked;
            this.updateModel();
        }
    }

    /**
     * Returns true if checked
     */
    isChecked(): boolean {
        if (this.binary) {
            return this.model;
        } else {
            return !!this.model && this.model.indexOf(this.value) > -1;
        }
    }

    /**
     * Removes value from model when in non binary state
     */
    removeValue(): void {
        this.model = this.model.filter(val => val !== this.value);
    }

    /**
     * Adds value to model when in non binary state
     */
    addValue(): void {
        if (this.model) {
            this.model = [...this.model, this.value];
        } else {
            this.model = [this.value];
        }
    }

    /**
     * Handles focus event
     */
    onFocus(event): void {
        this.focused = true;
    }

    /**
     * Handles blur event
     */
    onBlur(event): void {
        this.focused = false;
        this.blur.emit(event);
        this.onModelTouched();
    }

    /**
     * Setter for disabled
     */
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    writeValue(model: any): void {
        this.model = model;
        this.checked = this.isChecked();
        this.cd.markForCheck();
    }

    registerOnChange(fn: Function): void {
        this.onModelChange = fn;
    }

    registerOnTouched(fn: Function): void {
        this.onModelTouched = fn;
    }

    ngOnInit(): void {
        // just for label lookup, added it to ngOnInit so it just runs one time
        if (this.labelLookup) {
            let lookupObservable: Observable<any[]>;
            if (!(this.labelLookup instanceof Observable)) {
                lookupObservable = this.labelLookup([this.value]);
            } else {
                lookupObservable = this.labelLookup;
            }

            lookupObservable.pipe(filter(x => !!x)).subscribe(fullObjects => {
                this.label = fullObjects.find(obj => obj?.[this.labelLookupKey] === this.value)?.[this.labelLookupValue];
            });
        }
    }
}
